import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ENV } from '@envs/environment';
import { AppState } from '@services/core/app-state';
import { EnvironmentDynamicServiceService } from '../monitoring-ia/services/environment-dynamic-service.service';

export interface Ejecutivo {
  idEjecutivo: string;
  Nombre: string;
}

export interface EjecutivosResponse {
  success: boolean;
  total: number;
  ejecutivos: Ejecutivo[];
}

export interface FechaHora {
  date: { year: number; month: number; day: number };
  time: { hour: number; minute: number; second: number; nano: number };
}

export interface Seguimiento {
  IdSeguimiento: string;
  IdProspecto: string;
  Prospecto: string;
  IdTipoActividad: string;
  TipoActividad: string;
  IdTipoActividadDetalle: string;
  TipoActividadDetalle: string;
  Programada: FechaHora;
  MaxProgramada: FechaHora;
  Cumplida: FechaHora;
  IdPropietario: string;
  IdEjecutivo: string;
  EstaCumplida: 'Si' | 'No';
  Reprogramaciones: number;
  IdAuto?: string;
  Auto?: string;
  Imagen?: string;
}

export interface MultiagendaResponse {
  success: boolean;
  total: number;
  seguimientos: Seguimiento[];
}

export interface MultiagendaRequest {
  idDistribuidor: string;
  idEjecutivo: string;
  fechaInicio: string;
  fechaFin: string;
  idAuto?: string;
}

export interface Tarea {
  IdSeguimiento: string;
  IdProspecto: string;
  Prospecto: string;
  IdPropietario: string;
  TipoActividadDetalle: string;
  Programada: FechaHora;
  Cumplida: FechaHora;
  EstaCumplida: 'Si' | 'No';
  Ejecutivo: string;
}

export interface TareasResponse {
  success: boolean;
  total: number;
  tareas: Tarea[];
}

export interface TareasRequest {
  idDistribuidor: string;
  idUsuario: string;
  fechaInicio: string;
  fechaFin: string;
  mostrar: 'pendientes' | 'del-dia';
  filtrar: 'tareas-otros' | 'mis-tareas' | 'todas';
}

export interface ProspectoBusqueda {
  Nombre: string;
  IdProspecto: string;
}

export interface BuscarProspectoResponse {
  success: boolean;
  total: number;
  prospectos: ProspectoBusqueda[];
}

export interface AutoDemo {
  Auto: string;
  Producto: string;
  IdAuto: string;
}

export interface AutosDemoResponse {
  success: boolean;
  total: number;
  autos: AutoDemo[];
}

export interface ReprogramarSeguimientoRequest {
  idDistribuidor: string;
  idPropietario: string;
  idSeguimiento: string;
  fechaInicio: string;
  fechaFin: string;
}

export interface PostReprogramacionRequest {
  idDistribuidor: string;
  idSeguimiento: string;
  programada: string;
  observaciones: string;
  tipoEvaluacion: string;
}

export interface PostReprogramacionResponse {
  message: string;
  success: boolean;
}

export interface SeguimientoReprogramar {
  Prospecto: string;
  IdProspecto: string;
  EstaCumplida: 'Si' | 'No';
  IdPropietario: string;
  TipoActividadDetalle: string;
  IdSeguimiento: string;
  Programada: FechaHora;
  Cumplida: FechaHora;
  Ejecutivo: string;
}

export interface ReprogramarSeguimientoResponse {
  success: boolean;
  total: number;
  seguimientos: SeguimientoReprogramar[];
}

export interface DesbloquearProspectoRequest {
  idDistribuidor: string;
  idProspecto: string;
  observacion: string;
  idUsuario: string;
}

export interface DesbloquearProspectoResponse {
  success: boolean;
}

export interface CatalogoItem {
  id: string;
  nombre: string;
}

export interface GetCatalogosResponse {
  success: boolean;
  message: string;
  catalogos: CatalogoItem[];
}

export interface RechazarProspectoRequest {
  idDistribuidor: string;
  idProspecto: string;
  idMotivoRechazo: string;
  observacion: string;
  idUsuario: string;
}

export interface RechazarProspectoResponse {
  success: boolean;
  message?: string;
}

export interface MotivoRechazo {
  IdMotivoDeRechazo: string;
  Nombre: string;
}

export interface TipoRechazo {
  IdTipoDeRechazo: string;
  Nombre: string;
}

export interface GetMotivosRechazoResponse {
  success: boolean;
  totalMotivos?: number;
  motivos?: MotivoRechazo[];
  totalTipos?: number;
  tipos?: TipoRechazo[];
  motivosDondeCompro?: MotivoRechazo[];
  motivosPorqueNoCompra?: MotivoRechazo[];
  mostrarRechazoAFuturo?: boolean;
  error?: string;
}

export interface MarcaRechazo {
  IdMarca: string;
  Nombre: string;
}

export interface GetMarcasRechazoResponse {
  success: boolean;
  total?: number;
  marcas?: MarcaRechazo[];
  error?: string;
}

export interface AutoRechazo {
  IdAuto: string;
  Nombre: string;
}

export interface GetAutosRechazoResponse {
  success: boolean;
  total?: number;
  autos?: AutoRechazo[];
  error?: string;
}

export interface GetTiposRechazoResponse {
  success: boolean;
  total?: number;
  tipos?: TipoRechazo[];
  error?: string;
}

export interface ConfiguracionAgendaResponse {
  success: boolean;
  AgendaIntervalo: number | null;
  AgendaInicio: number | null;
  AgendaFin: number | null;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MultiagendaService {
  private readonly http = inject(HttpClient);
  private readonly app = inject(AppState);
  private readonly envDynamic = inject(EnvironmentDynamicServiceService);

  private readonly localUrl = 'http://localhost:8080/ApisSeekopServices/api';
  private useLocalhost = false;

  get baseUrl(): string {
    return this.useLocalhost ? this.localUrl : this.envDynamic.get('URL_MULTIAGENDA');
  }

  constructor() {
    console.log('🎫 [MultiagendaService] URL inicial:', this.baseUrl);
  }

  getEjecutivos(idDistribuidor: string): Observable<EjecutivosResponse> {
    return this.http.post<EjecutivosResponse>(
      `${this.baseUrl}/SeekopGeneral/getEjecutivos`,
      { idDistribuidor }
    );
  }

  getMultiagenda(request: MultiagendaRequest): Observable<MultiagendaResponse> {
    return this.http.post<MultiagendaResponse>(
      `${this.baseUrl}/MultiAgenda/getMultiagenda`,
      request
    );
  }

  getMultiAgendaDemos(request: MultiagendaRequest): Observable<MultiagendaResponse> {
    return this.http.post<MultiagendaResponse>(
      `${this.baseUrl}/MultiAgenda/getMultiAgendaDemos`,
      request
    );
  }

  getTareas(request: TareasRequest): Observable<TareasResponse> {
    return this.http.post<TareasResponse>(
      `${this.baseUrl}/MultiAgenda/getTareas`,
      request
    );
  }

  buscarProspectoNombre(idDistribuidor: string, nombre: string, idEjecutivo: string): Observable<BuscarProspectoResponse> {
    return this.http.post<BuscarProspectoResponse>(
      `${this.baseUrl}/MultiAgenda/buscarProspectoNombre`,
      { idDistribuidor, nombre, idEjecutivo }
    );
  }

  getAutosDemo(idDistribuidor: string, esAutoDemo: string): Observable<AutosDemoResponse> {
    return this.http.post<AutosDemoResponse>(
      `${this.baseUrl}/MultiAgenda/getAutosDemo`,
      { idDistribuidor, esAutoDemo }
    );
  }

  getReprogramarSeguimiento(request: ReprogramarSeguimientoRequest): Observable<ReprogramarSeguimientoResponse> {
    return this.http.post<ReprogramarSeguimientoResponse>(
      `${this.baseUrl}/MultiAgenda/getReprogramarSeguimiento`,
      request
    );
  }

  postReprogramacion(request: PostReprogramacionRequest): Observable<PostReprogramacionResponse> {
    return this.http.post<PostReprogramacionResponse>(
      `${this.app.ENV.MTY_SICOP}/Api/Bot/getReprogramacion`,
      request
    );
  }

  desbloquearProspecto(request: DesbloquearProspectoRequest): Observable<DesbloquearProspectoResponse> {
    return this.http.post<DesbloquearProspectoResponse>(
      `${this.baseUrl}/SeekopGeneral/desbloquearProspecto`,
      request
    );
  }

  getCatalogos(idDistribuidor: string, tipoCatalogo: string, idMarca: string): Observable<GetCatalogosResponse> {
    console.log('getCatalogos Rechazo', `${this.app.ENV.MTY_SICOP}/Api/Bot/getCatalogos?idDistribuidor=${idDistribuidor}&tipoCatalogo=${tipoCatalogo}&idMarca=${idMarca}`);
    return this.http.get<GetCatalogosResponse>(
      `${this.app.ENV.MTY_SICOP}/Api/Bot/getCatalogos?idDistribuidor=${idDistribuidor}&tipoCatalogo=${tipoCatalogo}&idMarca=${idMarca}`
    );
  }

  getMotivosRechazo(idDistribuidor: string, idEjecutivo: string): Observable<GetMotivosRechazoResponse> {
    return this.http.post<GetMotivosRechazoResponse>(
      `${this.baseUrl}/MultiAgenda/getMotivosRechazo`,
      { idDistribuidor, idEjecutivo }
    );
  }

  getMarcasRechazo(idDistribuidor: string): Observable<GetMarcasRechazoResponse> {
    return this.http.post<GetMarcasRechazoResponse>(
      `${this.baseUrl}/MultiAgenda/getMarcasRechazo`,
      { idDistribuidor }
    );
  }

  getAutosRechazo(idDistribuidor: string, idMarca: string): Observable<GetAutosRechazoResponse> {
    return this.http.post<GetAutosRechazoResponse>(
      `${this.baseUrl}/MultiAgenda/getAutosRechazo`,
      { idDistribuidor, idMarca }
    );
  }

  getTiposRechazo(idDistribuidor: string, idMotivoDeRechazo: string): Observable<GetTiposRechazoResponse> {
    return this.http.post<GetTiposRechazoResponse>(
      `${this.baseUrl}/MultiAgenda/getTiposRechazo`,
      { idDistribuidor, idMotivoDeRechazo }
    );
  }

  rechazarProspecto(request: RechazarProspectoRequest): Observable<RechazarProspectoResponse> {
    return this.http.post<RechazarProspectoResponse>(
      `${this.baseUrl}/SeekopGeneral/rechazarProspecto`,
      request
    );
  }

  getConfiguracionAgenda(idDistribuidor: string, idUsuario: string): Observable<ConfiguracionAgendaResponse> {
    return this.http.post<ConfiguracionAgendaResponse>(
      `${this.baseUrl}/MultiAgenda/getConfiguracionAgenda`,
      { idDistribuidor, idUsuario }
    );
  }
}
